import { useCallback, useMemo, useState } from "react";

import {
  createPageSizeSchema,
  PAGE_SIZE_OPTIONS,
  type PageSize,
  type PageSizeOptions,
} from "../schemas";

const DEFAULT_THRESHOLD = 5;
const ELLIPSIS = "ellipsis";

type PaginationStateOf<T extends PageSizeOptions> = {
  pageIndex: number;
  pageSize: PageSize<T>;
};

interface UsePaginationProps<
  T extends PageSizeOptions = typeof PAGE_SIZE_OPTIONS,
> {
  initialPageSize?: PageSize<T>;
  initialPageIndex?: number;
  threshold?: number;
  pageSizeOptions?: T;
}

interface UsePaginationReturn<
  T extends PageSizeOptions = typeof PAGE_SIZE_OPTIONS,
> {
  pagination: PaginationStateOf<T>;
  setPagination: (
    updater:
      | PaginationStateOf<T>
      | ((old: PaginationStateOf<T>) => PaginationStateOf<T>)
  ) => void;
  resetToFirstPage: () => void;
  getPageNumbers: (currentPage: number, totalPages: number) => PageNumber[];
}

type PageNumber = number | typeof ELLIPSIS;

function generatePageNumbers(
  currentPage: number,
  totalPages: number,
  threshold = DEFAULT_THRESHOLD
): PageNumber[] {
  if (totalPages <= 1) return [1];

  if (totalPages <= threshold) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const reservedSlots = 2;
  const availableSlots = Math.max(1, threshold - reservedSlots);

  const sidePages = Math.floor((availableSlots - 1) / 2);

  let startPage = Math.max(2, currentPage - sidePages);
  let endPage = Math.min(totalPages - 1, currentPage + sidePages);

  if (startPage === 2 && endPage < totalPages - 1) {
    endPage = Math.min(totalPages - 1, startPage + availableSlots - 1);
  } else if (endPage === totalPages - 1 && startPage > 2) {
    startPage = Math.max(2, endPage - availableSlots + 1);
  }

  const result: (number | typeof ELLIPSIS)[] = [1];

  if (startPage > 2) {
    if (startPage === 3 && result.length < threshold - 1) {
      result.push(2);
    } else {
      result.push(ELLIPSIS);
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    result.push(i);
  }

  if (endPage < totalPages - 1) {
    if (endPage === totalPages - 2 && result.length < threshold - 1) {
      result.push(totalPages - 1);
    } else {
      result.push(ELLIPSIS);
    }
  }

  if (totalPages > 1) {
    result.push(totalPages);
  }

  return result;
}

function usePagination<T extends PageSizeOptions = typeof PAGE_SIZE_OPTIONS>({
  initialPageIndex = 0,
  threshold = DEFAULT_THRESHOLD,
  pageSizeOptions,
  initialPageSize = (pageSizeOptions ?? PAGE_SIZE_OPTIONS)[0],
}: UsePaginationProps<T> = {}): UsePaginationReturn<T> {
  const options = (pageSizeOptions ?? PAGE_SIZE_OPTIONS) as T;

  const [pagination, setPaginationState] = useState<PaginationStateOf<T>>(
    () => ({
      pageIndex: initialPageIndex,
      pageSize: createPageSizeSchema(options).parse(initialPageSize),
    })
  );

  const setPagination = useCallback(
    (
      updater:
        | PaginationStateOf<T>
        | ((old: PaginationStateOf<T>) => PaginationStateOf<T>)
    ) => {
      setPaginationState(updater);
    },
    []
  );

  const resetToFirstPage = useCallback(() => {
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const getPageNumbers = useCallback(
    (currentPage: number, totalPages: number) => {
      return generatePageNumbers(currentPage, totalPages, threshold);
    },
    [threshold]
  );

  return useMemo(
    () => ({
      pagination,
      setPagination,
      resetToFirstPage,
      getPageNumbers,
    }),
    [pagination, setPagination, resetToFirstPage, getPageNumbers]
  );
}

export { usePagination, generatePageNumbers, ELLIPSIS };
export type {
  UsePaginationProps,
  UsePaginationReturn,
  PageNumber,
  PaginationStateOf,
};
